import { Fragment, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { manpowerApi, projectsApi, usersApi, type TravelAcknowledgmentRow } from '@/lib/apiClient';
import { fileOnceToJobFolder, GraphConsentError } from '@/lib/graphUpload';
/**
 * ⚠️ `fmtDateTime`, NOT `fmtDate`. `lib/dates.ts` says in as many words that
 * its date-only helpers are for fields the API serializes as DateTime but that
 * mean a calendar date, and must not be used for real moments like `createdAt`
 * — they discard the time and reinterpret the UTC day as a local one. A
 * signature recorded at 02:00 UTC would then display as the following day to
 * the person auditing it, which on this record is the whole point of the
 * column.
 */
import { fmtDateTime } from '@/lib/dates';
/**
 * ⚠️ PURE, AND DELIBERATELY NOT IN THIS FILE. Anything importing this card
 * pulls in `@/lib/apiClient` → `lib/msalConfig`, which reads
 * `window.location.origin` at module scope and dies in vitest's node
 * environment. Keeping these two functions outside that chain is what lets
 * them be tested at all. The module header says so at length.
 */
import {
  acknowledgmentUrl, canIssueLink, collectAllPages, FILING_SUBFOLDER, filingAction, needsReissueConfirmation,
} from './lib/travelAcknowledgmentLinks';

// The same delegated scope every other Job Folder upload uses.
const GRAPH_SCOPES = ['Files.ReadWrite.All'];

/**
 * Travel Acknowledgments on one job: who has been sent a link, who has signed,
 * and the control that issues a new one.
 *
 * ⚠️ THE PROJECT TRACKER SENDS NOTHING. The scheduler copies the link and
 * sends it themselves, by text or email (product owner, 2026-09-22). There is
 * deliberately no delivery here — no mail server, no SMS account, and nothing
 * that could fail silently between this screen and a person's phone.
 *
 * ⚠️ ISSUING IS NOT IDEMPOTENT, AND THE LIST IS WHY THIS CARD EXISTS. Each
 * click mints a new token, because a changed travel band must produce a new
 * acknowledgment rather than rewrite what somebody already agreed to. Without
 * the existing links in front of them, a scheduler who clicks twice has two
 * live links to the same person and no way to see it. So the rows come first
 * and the issue control asks for a confirmation when a live unsigned link for
 * that person is already on screen.
 *
 * ⚠️ A LINK IS A CREDENTIAL. The token is the whole of what the public page
 * checks — see `travel-acknowledgment.controller.ts` on the API. That is why
 * this card is gated on `manpower.edit` and not on a read permission, and why
 * nothing here writes a link into an export.
 */

const css = `
  .tak-card { background: #fff; border: 1px solid var(--ew-n-100); border-radius: var(--ew-r-lg); margin-bottom: var(--ew-s-4); }
  .tak-hd { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); }
  .tak-title { font: var(--ew-fw-semibold) var(--ew-fs-body) var(--ew-font); color: var(--ew-dark); margin: 0; }
  .tak-sub { font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-500); }
  .tak-body { padding: var(--ew-s-4); }
  .tak-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .tak-table th { text-align: left; padding: 8px var(--ew-s-3); white-space: nowrap; font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); border-bottom: 1px solid var(--ew-n-100); }
  .tak-table td { padding: 9px var(--ew-s-3); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: middle; }
  .tak-name { font-weight: var(--ew-fw-semibold); }
  .tak-muted { color: var(--ew-n-500); }
  .tak-signed { color: var(--ew-green); font-weight: var(--ew-fw-semibold); }
  .tak-pending { color: var(--ew-n-500); }
  .tak-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .tak-btn { font: var(--ew-fw-semibold) 12px var(--ew-font); border-radius: var(--ew-r-md); padding: 6px 12px; cursor: pointer; border: 1px solid var(--ew-n-200); background: #fff; color: var(--ew-dark); white-space: nowrap; }
  .tak-btn[disabled] { opacity: 0.5; cursor: default; }
  .tak-empty { font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-500); margin: 0; }
  .tak-caveat { margin-top: 10px; padding: 10px 12px; border-radius: var(--ew-r-md); background: var(--ew-safety-50, #FFF8E1); border: 1px solid var(--ew-safety, #E0A100); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-dark); }
  .tak-caveat p { margin: 0 0 6px; }
  .tak-caveat p:last-child { margin: 0; }
  .tak-caveat-row .tak-caveat { margin-top: 0; }
  .tak-error { margin-top: 10px; padding: 10px 12px; border-radius: var(--ew-r-md); background: #FEF2F2; border: 1px solid var(--ew-danger); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-dark); }
  .tak-picker { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .tak-filed { color: var(--ew-green); font-weight: var(--ew-fw-semibold); text-decoration: none; white-space: nowrap; }
  .tak-filed:hover { text-decoration: underline; }
  .tak-select { font: var(--ew-fw-regular) 12px var(--ew-font); padding: 6px 8px; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); background: #fff; color: var(--ew-dark); max-width: 260px; }
  @media (pointer: coarse) { .tak-btn, .tak-select { min-height: 34px; } }
`;

export default function TravelAcknowledgmentsCard({ projectId }: { projectId: number }) {
  const qc = useQueryClient();
  const { instance, accounts } = useMsal();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const canEdit = (me?.roles?.includes('Admin') ?? false)
    || (me?.permissions?.includes('manpower.edit') ?? false);

  const list = useQuery({
    queryKey: ['travel-acknowledgments', projectId],
    queryFn: () => manpowerApi.travelAcknowledgments(projectId),
    staleTime: 30_000,
    enabled: canEdit,
  });

  // The roster is the pool of people a link can be issued to. Active only —
  // an acknowledgment for former staff is not a thing anybody wants to send.
  const roster = useQuery({
    queryKey: ['travel-roster', 'for-acknowledgment'],
    // ⚠️ EVERY page, not the first. One `limit: 500` request silently dropped
    // everybody past the API's page ceiling — see `collectAllPages`.
    queryFn: async () => ({
      rows: await collectAllPages((offset) => manpowerApi.travelRoster({ limit: 500, offset })),
    }),
    staleTime: 5 * 60 * 1000,
    enabled: canEdit,
  });

  /**
   * The job's SharePoint folder, for filing signed PDFs. Same query key and
   * shape as the Documents and Drawings tabs, so it is one cached request.
   */
  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{ jobNumber: string; title: string; jobFolderUrl: string | null }>,
    staleTime: 60_000,
    enabled: canEdit,
  });
  /**
   * ⚠️ FROM `data`, NOT `isSuccess`. react-query keeps the last good project
   * when a background refetch fails and reports `isSuccess === false` while it
   * does; keying on `isSuccess` hid a folder the card already knew. undefined
   * only when there is no project data at all, and then `folderFailed` says
   * whether that is a load or a failure — see `filingAction`. (Codex, P2 on #559.)
   */
  const jobFolderUrl = project.data ? (project.data.jobFolderUrl ?? null) : undefined;
  const folderFailed = project.isError && !project.data;

  const [rosterId, setRosterId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<number | null>(null);

  /**
   * ⚠️ A SYNCHRONOUS LOCK ON ISSUING, because every other guard here is a
   * rendering. `issue.isPending` in the click handler is read from the LAST
   * render — and react-query does not tell React the mutation started until
   * later: its notify manager defaults to `setTimeout(0)` and
   * @tanstack/react-query keeps that default (both checked at the locked
   * 5.100.6). So two activations landing in that gap — a touchscreen
   * double-tap, assistive technology, two synthetic events — both saw "not
   * pending" and both minted a live token, with no prompt. (Codex, P2 on #542.)
   *
   * A ref is written and read synchronously, so the second activation sees the
   * first one's lock however the renders fall.
   *
   * ⚠️ RELEASED IN AN EFFECT, NOT IN `onSettled`, and that is the half that is
   * easy to get wrong. By `onSettled` the refetched rows are in the cache, but
   * the handler a click would run is still the closure from BEFORE the
   * refetch until React re-renders — another `setTimeout(0)` away. Releasing
   * there reopens the same gap one step later, against rows that lack the link
   * just created. The effect runs only after a render in which the mutation
   * has settled and the list is not fetching, so the handler that render
   * installed is the one holding current rows.
   *
   * ⚠️ AND THE EFFECT IS KEYED ON A SETTLE COUNTER, NOT ON `isPending`. React
   * may never render `isPending === true` at all: a mutation that fails within
   * microtasks (an auth interceptor rejecting, say) settles before the
   * `setTimeout(0)` flush, so the one render React does see already says "not
   * pending". An effect keyed on `isPending` would then never re-run, and the
   * lock would hold until the scheduler navigated away — trading a rare second
   * link for a dead button. Every settle bumps the counter, so the release is
   * guaranteed a render to run after.
   */
  const issuingRef = useRef(false);

  /**
   * Filing: which row is uploading, and the last filing error with the row it
   * is about. ⚠️ A REF LOCK as well as the state, for the reason `issuingRef`
   * gives — a double tap lands before React re-renders. A second upload would
   * be harmless (`fileOnceToJobFolder` adopts the existing file), but two
   * requests racing for one row's record is noise nobody needs.
   */
  const filingRef = useRef<number | null>(null);
  const [filingId, setFilingId] = useState<number | null>(null);
  const [fileError, setFileError] = useState<{ id: number; message: string } | null>(null);
  const [settled, setSettled] = useState(0);

  const issue = useMutation({
    mutationFn: ({ id }: { id: number }) =>
      manpowerApi.createTravelAcknowledgment(id, projectId),
    onSuccess: () => {
      setError(null);
      setRosterId('');
      /**
       * ⚠️ RETURNED, so the mutation stays pending until the refetch settles
       * rather than reporting success over a list that has not caught up.
       *
       * Belt and braces, NOT the guarantee: `canIssueLink` refuses while
       * `list.isFetching` regardless of what this callback does, and that gate
       * is what actually closes the window. Resting the invariant on the exact
       * awaiting behaviour of a library callback would be trusting the thing
       * that is hardest to check.
       */
      return qc.invalidateQueries({ queryKey: ['travel-acknowledgments', projectId] });
    },
    onError: (e: Error) => {
      /**
       * ⚠️ THE API'S OWN MESSAGE, NOT A GENERIC ONE. A refusal here says WHY
       * no entitlement can be stated — no travel band on the job, a job title
       * nobody has ruled on, conflicting Paycor rows — and names who can fix
       * it. That sentence is the entire value of the error; replacing it with
       * "Could not create link" sends the scheduler to ask an engineer.
       */
      setError(e.message);
    },
    // Schedules the render the lock is released after — not the release itself.
    onSettled: () => setSettled((n) => n + 1),
  });

  // Releases `issuingRef` once a render after the settle shows the list is not
  // fetching — see its comment for why here, and why not keyed on isPending.
  useEffect(() => {
    if (!list.isFetching) issuingRef.current = false;
  }, [settled, list.isFetching]);

  if (!canEdit) return null;

  /**
   * ⚠️ `rows` IS ONLY MEANINGFUL ONCE `list` HAS SUCCEEDED. The `?? []`
   * fallback is what `needsReissueConfirmation` would otherwise be asked
   * about mid-flight, and an empty list is indistinguishable from "this
   * person has no live link". Everything that depends on knowing what has
   * already been issued keys off `listLoaded`, never off `rows` alone.
   */
  const listLoaded = list.isSuccess;
  /**
   * ⚠️ `isFetching`, NOT `isLoading`. `isLoading` is only the FIRST load;
   * `isFetching` is any fetch, including the background refetch after an issue
   * — which is exactly the window where the rows on screen are stale but the
   * query still reports success.
   */
  const listFetching = list.isFetching;
  const rows = list.data ?? [];
  const people = (roster.data?.rows ?? []).filter((r) => r.isActive);

  async function copy(row: TravelAcknowledgmentRow) {
    const url = acknowledgmentUrl(window.location.origin, row.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(row.id);
      window.setTimeout(() => setCopied((c) => (c === row.id ? null : c)), 2000);
    } catch {
      // Clipboard access is refused outright in some mobile browsers and over
      // plain http. A prompt is not elegant, but it is the difference between
      // the scheduler having the link and not.
      window.prompt('Copy this link and send it to them:', url);
    }
  }

  async function getGraphToken(): Promise<string> {
    try {
      const r = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account: accounts[0] });
      return r.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        const r = await instance.acquireTokenPopup({ scopes: GRAPH_SCOPES });
        return r.accessToken;
      }
      throw new GraphConsentError('Graph consent required');
    }
  }

  /**
   * File one signed acknowledgment: the server builds the PDF from the frozen
   * record, this browser uploads it with the scheduler's own SharePoint access
   * into the job's "Travel Acknowledgments" folder (created the first time),
   * and the server records the link.
   *
   * ⚠️ UPLOAD, THEN RECORD — AND A RETRY IS SAFE. If recording fails after the
   * upload, the file is in SharePoint and the row still says unfiled. Filing
   * again uses the same deterministic name, finds that file, and records it,
   * rather than uploading a second copy. The error says so.
   */
  async function onFile(row: TravelAcknowledgmentRow) {
    if (filingRef.current != null) return;
    if (filingAction(row, jobFolderUrl, folderFailed) !== 'file' || !jobFolderUrl || !row.pdfFileName) return;
    filingRef.current = row.id;
    setFilingId(row.id);
    setFileError(null);
    let uploaded = false;
    try {
      /**
       * ⚠️ THE TOKEN FIRST, BEFORE ANY OTHER ROUND TRIP. If a silent token is
       * refused, MSAL opens a sign-in popup — and browsers allow a popup only
       * while the click's user activation lasts. Fetching the PDF first could
       * outlast it, so every filing by someone needing consent or a fresh
       * sign-in would be popup-blocked. (Codex, P2 on #559.) The Drawings tab
       * takes its token first for the same reason.
       */
      const token = await getGraphToken();
      const blob = await manpowerApi.travelAcknowledgmentPdf(row.id);
      const file = new File([blob], row.pdfFileName, { type: 'application/pdf' });
      const result = await fileOnceToJobFolder({
        getToken: async () => token, jobFolderUrl, subfolder: FILING_SUBFOLDER, fileName: row.pdfFileName, file,
      });
      uploaded = true;
      await manpowerApi.markTravelAcknowledgmentFiled(row.id, result.webUrl);
      await qc.invalidateQueries({ queryKey: ['travel-acknowledgments', projectId] });
    } catch (e) {
      /**
       * ⚠️ A 409 ON RECORDING IS "SOMEBODY ELSE FILED IT FIRST", not a failure
       * to retry — the refetch below turns the row into their link. Telling the
       * scheduler to file again there contradicts a row with no button left.
       * (Codex, P2 on #559.)
       */
      const conflict = uploaded && (e as { status?: number }).status === 409;
      const message = e instanceof GraphConsentError
        ? 'SharePoint access is still being set up (admin consent pending). Try again once it’s granted.'
        : conflict
          ? `Someone else filed this one first, so their link is kept. ${(e as Error).message}`
          : uploaded
          ? `The PDF is in SharePoint, but the Project Tracker couldn't record it: ${(e as Error).message} `
            + 'File it again to record it — the same file is used, not a second copy.'
          : `Filing failed: ${(e as Error).message}`;
      setFileError({ id: row.id, message });
      // A 409 from recording means somebody else filed it first — show theirs.
      void qc.invalidateQueries({ queryKey: ['travel-acknowledgments', projectId] });
    } finally {
      filingRef.current = null;
      setFilingId(null);
    }
  }

  function onIssue() {
    // ⚠️ Checked here as well as on the button. A disabled attribute is a
    // rendering, not an invariant — and what it is guarding is a second live
    // link to somebody who already has one.
    // The synchronous lock first: the check below reads render-time state.
    if (issuingRef.current) return;
    if (!canIssueLink({ rosterId, issuing: issue.isPending, listLoaded, listFetching })) return;
    const id = Number(rosterId);
    if (!Number.isFinite(id) || id <= 0) return;
    const name = people.find((p) => p.id === id)?.name ?? null;
    if (needsReissueConfirmation(rows, id)) {
      const who = name ?? 'this person';
      const ok = window.confirm(
        `${who} already has a link that has not been signed. Issuing a new one replaces it, `
        + 'and the earlier link will stop working. Issue a new one?',
      );
      if (!ok) return;
    }
    // The previous attempt's error is not about this one.
    setError(null);
    // Taken AFTER the confirm, so cancelling the prompt leaves issuing open.
    issuingRef.current = true;
    issue.mutate({ id });
  }

  return (
    <div className="tak-card">
      <style>{css}</style>
      <div className="tak-hd">
        <div>
          <h3 className="tak-title">Travel Acknowledgments</h3>
          {/* Says plainly that nothing is sent from here. A screen with an
              "Issue" button and a list of names reads as an outbox unless it
              is told not to. */}
          <div className="tak-sub">
            Issue a link, then send it yourself by text or email. The Project Tracker does not send it.
          </div>
        </div>
        <div className="tak-picker">
          <select
            className="tak-select"
            value={rosterId}
            onChange={(e) => {
              setRosterId(e.target.value);
              // An error is about the person whose issue failed. Left up, it
              // would read as being about whoever is picked next — and most
              // refusals are about the person (no name, an unresolved
              // title). (Codex, P2 on #542.)
              setError(null);
            }}
            aria-label="Person to issue a Travel Acknowledgment to"
          >
            <option value="">Choose someone…</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>{p.name ?? `Roster row ${p.id}`}</option>
            ))}
          </select>
          <button
            className="tak-btn"
            type="button"
            onClick={onIssue}
            disabled={!canIssueLink({ rosterId, issuing: issue.isPending, listLoaded, listFetching })}
          >
            {issue.isPending ? 'Issuing…' : 'Issue link'}
          </button>
        </div>
      </div>

      <div className="tak-body">
        {list.isLoading && <p className="tak-empty">Loading…</p>}

        {/*
          ⚠️ SURFACED, AND IT WAS NOT BEFORE. A failed list left `isLoading`
          false and `rows` empty, so this card said "none have been issued on
          this job yet" — asserting an emptiness it had never established, to
          somebody about to decide whether to issue one. (Codex, P2 on #542.)
        */}
        {list.isError && (
          <p className="tak-error">
            The acknowledgments already issued on this job could not be loaded, so this
            card cannot tell you what is outstanding. Issuing is disabled until it does —
            reload the page to try again.
          </p>
        )}

        {/*
          ⚠️ THE ROSTER IS SURFACED FOR THE SAME REASON THE LIST IS, AND IT WAS
          NOT. The dropdown reads `roster.data?.rows ?? []`, so a FAILED roster
          query and an EMPTY roster render identically — a picker with nobody in
          it and nothing on screen saying which. That ambiguity cost a round of
          diagnosis by hand on dev (2026-09-23); in production it would read as
          "there is nobody to send this to", which is a different and much
          worse claim than "this did not load".

          ⚠️ AND IT IS GATED ON AN EMPTY PICKER, NOT ON `isError` ALONE.
          react-query keeps the last successful rows when a REFETCH fails, so
          `isError` can be true while the dropdown is fully populated and
          usable. This banner over a working picker would say "there is nobody
          to choose from" when there plainly is — a false claim of exactly the
          kind this block was added to prevent, which is worse than the silence
          it replaced.

          ⚠️ THAT IS MEASURED, NOT ASSUMED, and the distinction is the whole
          lesson of this branch: five defects here began as confident reasoning
          in a comment that nobody tested. Run against `@tanstack/query-core`
          5.100.6 — the version this repo locks — a query that succeeds and
          then fails a refetch reports:

              after success         status=success  isError=false  isSuccess=true   data=PRESENT
              after failed refetch  status=error    isError=true   isSuccess=false  data=PRESENT

          So the stale-rows case is reachable, and `isSuccess` going false is
          also why the `list.isError` block above can keep its wording: it
          claims issuing is disabled, `listLoaded` is `list.isSuccess`, and that
          is false in exactly this state.

          With no options `rosterId` stays empty and `canIssueLink` refuses, so
          in the case this banner actually renders, issuing is blocked too. That
          reasoning does NOT extend to the stale-rows case, which is why the
          condition is narrowed rather than the comment widened. Issuing from a
          stale roster is bounded: the API refuses a deactivated person by name.
        */}
        {roster.isError && people.length === 0 && (
          <p className="tak-error">
            The travel roster could not be loaded, so there is nobody to choose from. This is
            not the same as an empty roster — reload the page to try again.
          </p>
        )}

        {/* The honest empty case, which the failure above is otherwise
            indistinguishable from. Keyed on `isSuccess` for the same reason
            the acknowledgments list is: only a query that came back can say
            a roster is empty. */}
        {roster.isSuccess && people.length === 0 && (
          <p className="tak-empty">
            Nobody is on the travel roster, so no link can be issued. The roster is on the
            Manpower page, under Gap Analysis.
          </p>
        )}

        {/* ⚠️ `isSuccess`, not `!isLoading`. Only a query that actually came
            back can say the list is empty. */}
        {list.isSuccess && rows.length === 0 && (
          <p className="tak-empty">No Travel Acknowledgments have been issued on this job yet.</p>
        )}

        {rows.length > 0 && (
          <table className="tak-table ew-cards">
            <thead>
              <tr>
                <th>Name</th>
                <th>Band</th>
                <th>Issued</th>
                <th>Signed</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                <tr>
                  <td className="tak-name" data-label="Name">{r.name ?? '—'}</td>
                  {/* The band the acknowledgment was issued against, which is
                      the one the person agreed to — not necessarily the job's
                      band today. That is the whole reason it is stored on the
                      row rather than read from the project. */}
                  <td data-label="Band">{r.travelBand ?? '—'}</td>
                  <td className="tak-muted" data-label="Issued">{fmtDateTime(r.createdAt)}</td>
                  <td data-label="Signed">
                    {r.signedAt
                      ? <span className="tak-signed">{r.signedName} · {fmtDateTime(r.signedAt)}</span>
                      : r.replaced
                        ? <span className="tak-pending">Replaced by a newer link</span>
                        : <span className="tak-pending">Not signed</span>}
                  </td>
                  <td>
                    {/* A replaced link no longer works, so it is not offered
                        to be copied and sent to somebody. */}
                    {!r.replaced && (
                      <div className="tak-actions">
                        <button className="tak-btn" type="button" onClick={() => void copy(r)}>
                          {copied === r.id ? 'Copied' : 'Copy link'}
                        </button>
                        {(() => {
                          const action = filingAction(r, jobFolderUrl, folderFailed);
                          if (action === 'filed') {
                            return (
                              <a
                                className="tak-filed"
                                href={r.filedUrl!}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={r.filedAt
                                  ? `Filed${r.filedByName ? ` by ${r.filedByName}` : ''} ${fmtDateTime(r.filedAt)}`
                                  : undefined}
                              >
                                Filed · Open in SharePoint
                              </a>
                            );
                          }
                          if (action === 'folder-error') {
                            return (
                              <span className="tak-muted">
                                Couldn&apos;t load this job&apos;s SharePoint folder — reload the page to file it.
                              </span>
                            );
                          }
                          if (action === 'no-folder') {
                            return (
                              <span className="tak-muted" title="Link or create it on the Documents tab.">
                                No SharePoint folder on this job
                              </span>
                            );
                          }
                          if (action === 'file' || action === 'loading') {
                            return (
                              <button
                                className="tak-btn"
                                type="button"
                                onClick={() => void onFile(r)}
                                disabled={action === 'loading' || filingId != null}
                              >
                                {filingId === r.id ? 'Filing…' : 'File to SharePoint'}
                              </button>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </td>
                </tr>
                {/*
                  ⚠️ THE ONLY PLACE THE ENGINE'S CAVEATS REACH A HUMAN. They
                  are raised on a STATED entitlement — most seriously that
                  exempt status is recorded nowhere, so a management traveller
                  may not be eligible at all. The signing page withholds them
                  on purpose (they read as hedging on a form somebody is
                  signing), so if this card drops them the warning reaches
                  nobody.

                  Read from each ROW, which the API takes from the frozen
                  record. They used to live in this card's state from the issue
                  response: a reload, a revisit or a later failed issue lost
                  them, while the link stayed right here ready to copy and
                  send. (Codex, P2 on #542.) Beneath the link they are about,
                  and naming its person, so no warning sits beside somebody it
                  does not describe — the earlier misattribution fix, now
                  structural rather than remembered.

                  Not on a replaced link: it no longer works, and the newer
                  link carries its own.
                */}
                {/* Not once the row is filed: whatever went wrong, the job now
                    has its copy, and a retry instruction would point at a
                    button that is no longer there. (Codex, P2 on #559.) */}
                {fileError?.id === r.id && !r.filedUrl && (
                  <tr className="tak-caveat-row">
                    <td colSpan={5}><div className="tak-error">{fileError.message}</div></td>
                  </tr>
                )}
                {r.caveats.length > 0 && !r.replaced && (
                  <tr className="tak-caveat-row">
                    <td colSpan={5}>
                      <div className="tak-caveat">
                        <p><strong>{r.name ? `About ${r.name}:` : 'About this link:'}</strong></p>
                        {r.caveats.map((c) => <p key={c}>{c}</p>)}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}

        {error && <p className="tak-error">{error}</p>}
      </div>
    </div>
  );
}
